import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import { ChatContext } from './chat-context';
import {
  createChatTokenSimple,
  createChatTokenTemplateSimple,
  createChatTokenConfigSimple,
  getAiSaver, getTemplateCode, getTemplateCodeV2,
  commitCreateDoc as api_commitCreateDoc, getSiteConfig,
} from '../../../cms-api/ai';
import { getPage, getPost } from '../../../cms-api/page';
import { getTemplate } from '../../../cms-api/template';
import { useSiteContext } from '../../../components/site/context/site-context';
import { createExponentialBackoff, createTokenRetryHandler } from '../../../utils/retry-utils';

const initialState = {
  loading: false,
};

const MSG_ID_RE = /^\[MSG_ID:([0-9a-fA-F-]{36})\]\s/;

export function ChatProvider({ children, type_str, config_data, set_template_code, get_template_code, get_site_config_data }) {

  const { site, getTempConfig, saveTempConfig } = useSiteContext();
  const { enqueueSnackbar } = useSnackbar();

  const [type, setType] = useState(type_str);

  const [config, setConfig] = useState(config_data);

  const [state, setState] = useState(initialState);

  const [isSending, setIsSending] = useState(false);

  const [socketUrl, setSocketUrl] = useState(null);

  const [createToken, setCreateToken] = useState(null);

  const [createTokenLoading, setCreateTokenLoading] = useState(false);

  const [lastMessageId, setLastMessageId] = useState(null);
  const [intermediateProcessMessage, setIntermediateProcessMessage] = useState(null);

  const [templateHistory, setTemplateHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [hasTemplateChanges, setHasTemplateChanges] = useState(false);

  const seenMessageIdsRef = useRef(new Set());

  const addMessageFuncRef = useRef(null)

  const messageStatusRef = useRef(null)

  const formatMessageRef = useRef(null)

  const websocketBackoffRef = useRef(createExponentialBackoff(3000, 30000))

  const tokenRetryHandlerRef = useRef(createTokenRetryHandler(2000, 60000, 5))

  const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    onError: (event) => {
      console.error("WebSocket error:", event);
    },
    onClose: (event) => {
      console.warn("WebSocket close:", event);
    },
    onOpen: () => {
      console.log("WebSocket connected successfully");
      websocketBackoffRef.current.reset();
    },
    shouldReconnect: (closeEvent) => {
      // const backoff = websocketBackoffRef.current;
      // if (backoff.hasReachedMaxRetries()) {
      //   console.error("WebSocket max reconnection attempts reached");
      //   return false;
      // }
      console.warn("WebSocket close event:", closeEvent);
      return true;
    },
    // reconnectAttempts: 10,
    reconnectInterval: () => {
      const delay = websocketBackoffRef.current.getDelay();
      const safeDelay = delay ?? 30000;
      console.warn(`WebSocket reconnecting in ${safeDelay}ms`);
      return safeDelay;
    },
    retryOnError: true,
  });

  const setAddMessageFunc = useCallback((func) => {
    addMessageFuncRef.current = func;
  }, []);

  const callAddMessageFunc = useCallback((...args) => {
    if (addMessageFuncRef.current) {
      addMessageFuncRef.current(...args);
    }
  }, []);

  const setMessageStatusFunc = useCallback((func) => {
    messageStatusRef.current = func;
  }, [])

  const callMessageStatusFunc = useCallback((...args) => {
    if (messageStatusRef.current) {
      messageStatusRef.current(...args);
    }
  }, [])

  const setFormatMessageFunc = useCallback((func) => {
    formatMessageRef.current = func;
  }, [])

  const callFormatMessageFunc = useCallback((...args) => {
    if (formatMessageRef.current) {
      formatMessageRef.current(...args);
    }
  }, [])

  const createTokenCallbackSimple = async (site_id) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      const createTokenData = await createChatTokenSimple({
        site_id,
        address: saverData.address,
        token: saverData.token,
      });

      setCreateToken(createTokenData);
      setSocketUrl(`${saverData.address}/chat/${createTokenData.chat_token}/simple/chat`);
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      console.error("Error creating chat token:", error);
      const retryHandler = tokenRetryHandlerRef.current;
      
      if (!retryHandler.hasReachedMaxRetries()) {
        const success = await retryHandler.retry(executeTokenCreation);
        if (!success) {
          setCreateTokenLoading(false);
          setIsSending(false);
        }
      } else {
        setCreateTokenLoading(false);
        setIsSending(false);
      }
    }
  }
  
  const createTokenCallbackConfigSimple = async (site_id) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      const createTokenData = await createChatTokenConfigSimple({
        site_id,
        address: saverData.address,
        token: saverData.token,
      });

      setCreateToken(createTokenData);
      setSocketUrl(`${saverData.address}/config/chat/${createTokenData.chat_token}/simple/chat`);
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      console.error("Error creating config chat token:", error);
      const retryHandler = tokenRetryHandlerRef.current;
      
      if (!retryHandler.hasReachedMaxRetries()) {
        const success = await retryHandler.retry(executeTokenCreation);
        if (!success) {
          setCreateTokenLoading(false);
          setIsSending(false);
        }
      } else {
        setCreateTokenLoading(false);
        setIsSending(false);
      }
    }
  }


  const createTokenCallbackTemplateSimple = async (site_id,
                                                   page_pkg_code,
                                                   template_pkg_code,
                                                   page_type,
                                                   is_list_page,
                                                   template_page_id
  ) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      const createTokenData = await createChatTokenTemplateSimple({
        site_id,
        address: saverData.address,
        token: saverData.token,
        page_pkg_code,
        template_pkg_code,
        page_type,
        is_list_page,
        template_page_id,
      });

      setCreateToken(createTokenData);
      setSocketUrl(`${saverData.address}/template/chat/${createTokenData.chat_token}/simple/chat`);
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      console.error("Error creating template chat token:", error);
      const retryHandler = tokenRetryHandlerRef.current;
      
      if (!retryHandler.hasReachedMaxRetries()) {
        const success = await retryHandler.retry(executeTokenCreation);
        if (!success) {
          setCreateTokenLoading(false);
          setIsSending(false);
        }
      } else {
        setCreateTokenLoading(false);
        setIsSending(false);
      }
    }
  }
  const createTokenCallbackTemplateSimpleV2 = async (site_id,
                                                   page_pkg_code,
                                                   template_pkg_code,
                                                   page_type,
                                                   is_list_page,
                                                   template_page_id
  ) => {
    const executeTokenCreation = async () => {
      const saverData = await getAiSaver();
      
      let template_layout_code = null;
      
      if (template_page_id) {
        try {
          const getPageFunc = page_type === 2 ? getPost : getPage;
          
          const pageData = await getPageFunc({
            site_id,
            page_id: template_page_id,
            page_pkg_code,
          });
          
          let tpl_code;
          if (page_type === 2) {
            tpl_code = is_list_page ? pageData.list_tpl_code : pageData.detail_tpl_code;
          } else {
            tpl_code = pageData.tpl_code;
          }
          
          if (tpl_code) {
            const templateData = await getTemplate({
              site_id,
              template_pkg_code,
              tpl_code,
            });
            
            template_layout_code = templateData.layout_code;
          }
        } catch (error) {
          console.error("Error fetching page data or template layout code:", error);
        }
      }
      
      const createTokenParams = {
        site_id,
        address: saverData.address,
        token: saverData.token,
        page_pkg_code,
        template_pkg_code,
        page_type,
        is_list_page,
        template_page_id,
      };
      
      if (template_layout_code) {
        createTokenParams.template_layout_code = template_layout_code;
      }
      
      const createTokenData = await createChatTokenTemplateSimple(createTokenParams);

      setCreateToken(createTokenData);
      setSocketUrl(`${saverData.address}/template/chat/${createTokenData.chat_token}/v2/simple/chat`);
      setIsSending(false);
      tokenRetryHandlerRef.current.reset();
    };

    try {
      await executeTokenCreation();
    } catch (error) {
      console.error("Error creating template chat token (v2):", error);
      const retryHandler = tokenRetryHandlerRef.current;

      if (!retryHandler.hasReachedMaxRetries()) {
        const success = await retryHandler.retry(executeTokenCreation);
        if (!success) {
          setCreateTokenLoading(false);
          setIsSending(false);
        }
      } else {
        setCreateTokenLoading(false);
        setIsSending(false);
      }
    }
  }


  const createTokenCallback = useCallback(async () => {
    if (!createTokenLoading && !createToken) {
      tokenRetryHandlerRef.current.reset();
      setCreateTokenLoading(true);

      try {
        if (type === "template-simple") {
          await createTokenCallbackTemplateSimple(
            site.site_id,
            config.page_pkg_code,
            config.template_pkg_code,
            config.page_type,
            config.is_list_page,
            config.template_page_id,
          );
        } else if (type === "template-simple-v2") {
          await createTokenCallbackTemplateSimpleV2(
            site.site_id,
            config.page_pkg_code,
            config.template_pkg_code,
            config.page_type,
            config.is_list_page,
            config.template_page_id,
          );
        } else if (type === "config-simple") {
          await createTokenCallbackConfigSimple(
            site.site_id,
          );
        } else {
          await createTokenCallbackSimple(site.site_id);
        }
      } finally {
        setCreateTokenLoading(false);
      }
    }
  }, [site, createToken, createTokenLoading, type, config]);

  const commitCreateDoc = useCallback(async (chat_token, doc, callbackOk) => {
    const saverData = await getAiSaver();
    const re_data = await api_commitCreateDoc({
      address: saverData.address,
      chat_token,
      cms_token: saverData.token,
      doc,
    });
    if (re_data.status === "OK") {
      callbackOk();
    }
  }, []);

  const getCode = useCallback(async (chat_token) => {

    if (type === "template-simple") {
      const saverData = await getAiSaver();
      const decode_code = await getTemplateCode({
        address: saverData.address,
        chat_token,
        cms_token: saverData.token,
      });

      set_template_code(decode_code.code);
    } else if (type === "template-simple-v2") {
      const saverData = await getAiSaver();
      const decode_code = await getTemplateCodeV2({
        address: saverData.address,
        chat_token,
        cms_token: saverData.token,
      });

      set_template_code(decode_code.code);
    }
    return "";
  }, [type, set_template_code]);

  const saveTemplateHistory = useCallback((code) => {
    if (!code) return;
    
    if (templateHistory.length > 0) {
      const lastHistoryItem = templateHistory[templateHistory.length - 1];
      if (lastHistoryItem.code === code) {
        return; // Don't add duplicate
      }
    }
    
    const newHistory = historyIndex >= 0 
      ? templateHistory.slice(0, historyIndex + 1)
      : [];
    
    const historyItem = {
      code,
      timestamp: new Date().getTime(),
      chatToken: createToken?.chat_token
    };
    
    const updatedHistory = [...newHistory, historyItem];
    
    const MAX_HISTORY_SIZE = 10;
    if (updatedHistory.length > MAX_HISTORY_SIZE) {
      updatedHistory.shift();
    }

    setTemplateHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
    setHasTemplateChanges(true);

    if (createToken?.chat_token) {
      const storageKey = `template_history_${createToken.chat_token}`;
      const historyData = {
        history: updatedHistory,
        historyIndex: updatedHistory.length - 1,
        hasChanges: true,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(storageKey, JSON.stringify(historyData));
    }
  }, [templateHistory, historyIndex, createToken]);


  const getCodeCallback = useCallback(async (chat_token) => {
    if (type === "template-simple" || type === "template-simple-v2") {
      await getCode(chat_token);
      
      if (get_template_code) {
        const newCode = get_template_code();
        saveTemplateHistory(newCode);
      }
      
      enqueueSnackbar('テンプレートコードが更新されました。履歴から前のバージョンを確認できます。', { 
        variant: 'success' 
      });
    }
  }, [type, getCode, saveTemplateHistory, enqueueSnackbar, get_template_code]);

  useEffect(() => {
    if (lastMessage?.data) {
      const raw = lastMessage.data;
      const m = raw.match(MSG_ID_RE);
      const msgId = m ? m[1] : null;
      if (msgId) {
        const set = seenMessageIdsRef.current;
        if (set.has(msgId)) {
          return;
        }
        if (set.size > 2000) set.clear();
        set.add(msgId);
      }

      const text = raw.replace(MSG_ID_RE, "");

      if (text.startsWith("ss-json:")) {
        const message = JSON.parse(text.substring(8));
        callFormatMessageFunc(message);
      } else if (text.startsWith("<start>:")) {
        setIsSending(true);
        setLastMessageId(text);
      } else if (text.startsWith("[INTERMEDIATE_PROCESS] ")) {
        const processMessage = text.substring(23);
        setIntermediateProcessMessage(processMessage);
        setIsSending(true);
      } else if (text.endsWith(":end") || text.endsWith(":wait")) {
        callMessageStatusFunc(text);
        if (text.endsWith(":end") || text === "create_doc_code:wait") {
          setIntermediateProcessMessage(null);

          if (type === "config-simple" && text.endsWith("config_edit:end")) {
            getAiSaver().then((saverData)=> {
              getSiteConfig({
                "address": saverData.address,
                "chat_token": createToken.chat_token,
                "cms_token": saverData.token,
              }).then((configData) => {
                saveTempConfig(configData.site_config);
              }).finally(()=>{
                setIsSending(false);
              })
            }).then(() => {
              setIsSending(false);
            })
          } else if (text.endsWith("create_code:end")) {
            getCodeCallback(createToken.chat_token);
            setIsSending(false);
          } else {
            setIsSending(false);
          }
        }
      } else {
        callAddMessageFunc(text);
        setIsSending(true);
        setIntermediateProcessMessage(null);
      }
    }
  }, [lastMessage, lastMessageId,
    callAddMessageFunc, callMessageStatusFunc,
    callFormatMessageFunc, type,
    createToken, saveTempConfig, getCodeCallback ]);

  useEffect(() => {
    if (createToken?.chat_token && (type === "template-simple" || type === "template-simple-v2")) {
      const storageKey = `template_history_${createToken.chat_token}`;
      const storedData = localStorage.getItem(storageKey);
      
      if (storedData) {
        try {
          const historyData = JSON.parse(storedData);
          setTemplateHistory(historyData.history || []);
          setHistoryIndex(historyData.historyIndex || -1);
          setHasTemplateChanges(historyData.hasChanges || false);
        } catch (error) {
          console.error('Error parsing template history:', error);
        }
      }
    }
  }, [createToken, type]);


  const memoizedValue = useMemo(
    () => ({
      loading: state.loading,
      isSending,
      createTokenLoading,
      setIsSending,
      sendMessage,
      lastMessage,
      createTokenCallback,
      getCodeCallback,
      type,
      readyState,
      createToken,
      set_template_code,
      get_template_code,
      get_site_config_data,
      commitCreateDoc,
      lastMessageId,
      setAddMessageFunc,
      setMessageStatusFunc,
      setFormatMessageFunc,
      templateHistory,
      historyIndex,
      hasTemplateChanges,
      saveTemplateHistory,
      config,
      intermediateProcessMessage,
    }),
    [
      state.loading,
      isSending,
      setIsSending,
      sendMessage,
      lastMessage,
      createTokenCallback,
      getCodeCallback,
      createTokenLoading,
      type,
      readyState,
      createToken,
      set_template_code,
      get_template_code,
      get_site_config_data,
      commitCreateDoc,
      lastMessageId,
      setAddMessageFunc,
      setMessageStatusFunc,
      setFormatMessageFunc,
      templateHistory,
      historyIndex,
      hasTemplateChanges,
      saveTemplateHistory,
      config,
      intermediateProcessMessage,
    ]
  );

  return <ChatContext.Provider value={memoizedValue}>{children}</ChatContext.Provider>;
}

ChatProvider.propTypes = {
  children: PropTypes.node,
  type_str: PropTypes.string.isRequired,
  config_data: PropTypes.object,
  set_template_code: PropTypes.func,
  get_template_code: PropTypes.func,
  get_site_config_data: PropTypes.func,
};
